import {
  Users,
  Search,
  Lightbulb,
  Package,
  TrendingUp,
  Target,
  Frown,
  Smile,
  Heart,
  MessageSquare,
  Truck,
  Gift,
  Zap,
  Sparkles,
  DollarSign,
  Wallet,
  Settings,
  Briefcase,
  Handshake,
  type LucideIcon,
} from 'lucide-react'
import { NAMESPACES } from '@/lib/ontology/namespaces'

const { market, business, solution } = NAMESPACES

// Entity definition for a discovery step
export interface DiscoveryEntity {
  classUri: string
  label: string
  localName: string
  icon: LucideIcon
  description: string
  tips: string[]
  required: boolean
  minCount?: number
}

// Phase definition
export interface DiscoveryPhase {
  id: string
  number: number
  title: string
  subtitle: string
  description: string
  icon: LucideIcon
  color: string
  bgColor: string
  entities: DiscoveryEntity[]
  linkedCanvas?: string
  completionMessage: string
}

// ============================================
// PHASE 1: Customer Discovery
// ============================================
export const PHASE_CUSTOMER_DISCOVERY: DiscoveryPhase = {
  id: 'customer-discovery',
  number: 1,
  title: 'Customer Discovery',
  subtitle: 'Who are your customers?',
  description: 'Start by identifying who you are building for. Define your target customer segments, create personas, and understand the industry context.',
  icon: Users,
  color: '#5c8a6b',
  bgColor: '#e0f5ef',
  entities: [
    {
      classUri: `${market}Industry`,
      label: 'Industry',
      localName: 'Industry',
      icon: TrendingUp,
      description: 'The economic sector you are operating in',
      tips: [
        'Use NAICS or SIC codes for precision',
        'Consider both primary and adjacent industries',
        'Research market size and growth rate',
      ],
      required: false,
    },
    {
      classUri: `${market}CustomerSegment`,
      label: 'Customer Segment',
      localName: 'CustomerSegment',
      icon: Users,
      description: 'A homogeneous group of customers with similar needs',
      tips: [
        'Start broad, then narrow down',
        'Define by demographics, behaviors, or needs',
        'Aim for 2-3 distinct segments initially',
      ],
      required: true,
      minCount: 1,
    },
    {
      classUri: `${market}Persona`,
      label: 'Persona',
      localName: 'Persona',
      icon: Target,
      description: 'An archetypal user or buyer representation',
      tips: [
        'Give them a name and backstory',
        'Include their goals and frustrations',
        'Base on real customer interviews',
      ],
      required: true,
      minCount: 1,
    },
  ],
  linkedCanvas: 'business-model-canvas',
  completionMessage: 'Great! You\'ve identified your target customers. Now let\'s understand their problems.',
}

// ============================================
// PHASE 2: Problem Validation
// ============================================
export const PHASE_PROBLEM_VALIDATION: DiscoveryPhase = {
  id: 'problem-validation',
  number: 2,
  title: 'Problem Validation',
  subtitle: 'What problems do they have?',
  description: 'Deeply understand the jobs your customers are trying to do, the pains they experience, and the gains they desire. This is the foundation of product-market fit.',
  icon: Search,
  color: '#c9594d',
  bgColor: '#fae9e7',
  entities: [
    {
      classUri: `${market}CustomerJob`,
      label: 'Customer Job',
      localName: 'CustomerJob',
      icon: Target,
      description: 'A task the customer needs to accomplish',
      tips: [
        'Think functional, social, and emotional jobs',
        'Use "When I... I want to... so that..."',
        'Prioritize by frequency and importance',
      ],
      required: true,
      minCount: 1,
    },
    {
      classUri: `${market}CustomerPain`,
      label: 'Customer Pain',
      localName: 'CustomerPain',
      icon: Frown,
      description: 'A frustration or obstacle experienced by a customer',
      tips: [
        'Rate by severity (extreme, moderate, mild)',
        'Look for blockers and inefficiencies',
        'Validate through customer interviews',
      ],
      required: true,
      minCount: 1,
    },
    {
      classUri: `${market}CustomerGain`,
      label: 'Customer Gain',
      localName: 'CustomerGain',
      icon: Smile,
      description: 'A desired outcome or benefit for a customer',
      tips: [
        'Required gains vs nice-to-have gains',
        'Include unexpected delighters',
        'Connect to emotional outcomes',
      ],
      required: true,
      minCount: 1,
    },
    {
      classUri: `${market}Competitor`,
      label: 'Competitor / Alternative',
      localName: 'Competitor',
      icon: Users,
      description: 'Existing solutions customers use today',
      tips: [
        'Include direct and indirect competitors',
        'Don\'t forget "do nothing" as an alternative',
        'Analyze their strengths and weaknesses',
      ],
      required: false,
    },
  ],
  linkedCanvas: 'value-proposition-canvas',
  completionMessage: 'You\'ve mapped out customer problems. Time to design your value proposition!',
}

// ============================================
// PHASE 3: Value Design
// ============================================
export const PHASE_VALUE_DESIGN: DiscoveryPhase = {
  id: 'value-design',
  number: 3,
  title: 'Value Design',
  subtitle: 'How will you create value?',
  description: 'Design your value proposition by defining what unique value you will deliver, how you will reach customers, and what relationships you will build.',
  icon: Lightbulb,
  color: '#ffc05f',
  bgColor: '#fdf6e0',
  entities: [
    {
      classUri: `${business}ValueProposition`,
      label: 'Value Proposition',
      localName: 'ValueProposition',
      icon: Heart,
      description: 'The bundle of value offered to a customer segment',
      tips: [
        'One value proposition per segment',
        'Clearly articulate the unique benefit',
        'Link to specific pains and gains',
      ],
      required: true,
      minCount: 1,
    },
    {
      classUri: `${business}Channel`,
      label: 'Channel',
      localName: 'Channel',
      icon: Truck,
      description: 'How you reach and deliver value to customers',
      tips: [
        'Map the customer journey phases',
        'Consider owned, partner, and earned channels',
        'Think awareness, purchase, and delivery',
      ],
      required: false,
    },
    {
      classUri: `${business}CustomerRelationship`,
      label: 'Customer Relationship',
      localName: 'CustomerRelationship',
      icon: MessageSquare,
      description: 'Type of relationship with each segment',
      tips: [
        'Self-service vs personal assistance',
        'Transactional vs long-term',
        'Community building opportunities',
      ],
      required: false,
    },
  ],
  linkedCanvas: 'value-proposition-canvas',
  completionMessage: 'Your value proposition is taking shape. Let\'s define the solution!',
}

// ============================================
// PHASE 4: Solution Design
// ============================================
export const PHASE_SOLUTION_DESIGN: DiscoveryPhase = {
  id: 'solution-design',
  number: 4,
  title: 'Solution Design',
  subtitle: 'What will you build?',
  description: 'Define your product or service, its key features, and how it relieves customer pains. Focus on the minimum viable product that validates your hypothesis.',
  icon: Package,
  color: '#3196e2',
  bgColor: '#e0eef8',
  entities: [
    {
      classUri: `${solution}Product`,
      label: 'Product / Service',
      localName: 'Product',
      icon: Gift,
      description: 'Your software or service product',
      tips: [
        'Start with the core value, not features',
        'Define the product type (SaaS, mobile, etc.)',
        'Keep initial scope minimal',
      ],
      required: true,
      minCount: 1,
    },
    {
      classUri: `${solution}Feature`,
      label: 'Feature',
      localName: 'Feature',
      icon: Sparkles,
      description: 'A specific capability of the product',
      tips: [
        'Prioritize by customer pain severity',
        'Focus on differentiators',
        'Define clear acceptance criteria',
      ],
      required: true,
      minCount: 1,
    },
    {
      classUri: `${solution}UserStory`,
      label: 'User Story',
      localName: 'UserStory',
      icon: Target,
      description: 'A feature from the end user perspective',
      tips: [
        'As a [persona], I want to [action] so that [benefit]',
        'Include acceptance criteria',
        'Estimate with story points',
      ],
      required: false,
    },
    {
      classUri: `${solution}Requirement`,
      label: 'Requirement',
      localName: 'Requirement',
      icon: Zap,
      description: 'A functional or non-functional requirement',
      tips: [
        'Separate functional and non-functional',
        'Define clear success criteria',
        'Consider security and performance',
      ],
      required: false,
    },
  ],
  linkedCanvas: 'positioning-canvas',
  completionMessage: 'Your solution is defined! Now let\'s complete the business model.',
}

// ============================================
// PHASE 5: Business Model
// ============================================
export const PHASE_BUSINESS_MODEL: DiscoveryPhase = {
  id: 'business-model',
  number: 5,
  title: 'Business Model',
  subtitle: 'How will you make money?',
  description: 'Complete your business model by defining revenue streams, cost structure, key activities, resources, and partnerships needed to operate.',
  icon: TrendingUp,
  color: '#8a847a',
  bgColor: '#f5f4f0',
  entities: [
    {
      classUri: `${business}RevenueStream`,
      label: 'Revenue Stream',
      localName: 'RevenueStream',
      icon: DollarSign,
      description: 'How you generate income from value propositions',
      tips: [
        'Define pricing mechanism (subscription, one-time, etc.)',
        'Consider willingness to pay research',
        'Link to specific customer segments',
      ],
      required: true,
      minCount: 1,
    },
    {
      classUri: `${business}CostStructure`,
      label: 'Cost Structure',
      localName: 'CostStructure',
      icon: Wallet,
      description: 'Costs incurred to operate the business model',
      tips: [
        'Identify fixed vs variable costs',
        'Focus on major cost drivers',
        'Consider cost-driven vs value-driven model',
      ],
      required: false,
    },
    {
      classUri: `${business}KeyActivity`,
      label: 'Key Activity',
      localName: 'KeyActivity',
      icon: Settings,
      description: 'Essential business actions',
      tips: [
        'Focus on activities for value delivery',
        'Include platform management if applicable',
        'Consider R&D and operations',
      ],
      required: false,
    },
    {
      classUri: `${business}KeyResource`,
      label: 'Key Resource',
      localName: 'KeyResource',
      icon: Briefcase,
      description: 'Assets required to operate the business model',
      tips: [
        'Physical, intellectual, human, or financial',
        'What makes your business work?',
        'Consider strategic assets',
      ],
      required: false,
    },
    {
      classUri: `${business}KeyPartnership`,
      label: 'Key Partnership',
      localName: 'KeyPartnership',
      icon: Handshake,
      description: 'Network of suppliers and partners',
      tips: [
        'Strategic alliances and joint ventures',
        'Supplier relationships',
        'Buyer-supplier partnerships',
      ],
      required: false,
    },
  ],
  linkedCanvas: 'business-model-canvas',
  completionMessage: 'Congratulations! You\'ve completed your discovery journey. Your business model is ready to test!',
}

// ============================================
// All phases in order
// ============================================
export const DISCOVERY_PHASES: DiscoveryPhase[] = [
  PHASE_CUSTOMER_DISCOVERY,
  PHASE_PROBLEM_VALIDATION,
  PHASE_VALUE_DESIGN,
  PHASE_SOLUTION_DESIGN,
  PHASE_BUSINESS_MODEL,
]

// Get phase by ID
export function getPhaseById(id: string): DiscoveryPhase | undefined {
  return DISCOVERY_PHASES.find((p) => p.id === id)
}

// Get next phase
export function getNextPhase(currentId: string): DiscoveryPhase | undefined {
  const currentIndex = DISCOVERY_PHASES.findIndex((p) => p.id === currentId)
  if (currentIndex === -1 || currentIndex === DISCOVERY_PHASES.length - 1) {
    return undefined
  }
  return DISCOVERY_PHASES[currentIndex + 1]
}

// Get previous phase
export function getPreviousPhase(currentId: string): DiscoveryPhase | undefined {
  const currentIndex = DISCOVERY_PHASES.findIndex((p) => p.id === currentId)
  if (currentIndex <= 0) {
    return undefined
  }
  return DISCOVERY_PHASES[currentIndex - 1]
}
